import {
  Linkedin,
  GalleryHorizontalEnd,
  Code,
  Brain,
  Heart,
  Palette,
} from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import pierceObrein from "../src/assets/pierce_obrien.webp";
import florAsef from "../src/assets/florencia_asef.webp";
import robSheridan from "../src/assets/rob_sheridan.webp";

interface Team {
  id: string;
  name: string;
  role: string;
  bio: string;
  image: string;
  linkedin?: string;
}

const teams: Team[] = [
  {
    id: "rob-sheridan",
    name: "Rob Sheridan",
    role: "Co-Founder & CEO",
    bio: "Rob is a qualified psychotherapist and a registered member of the Irish Association for Counselling and Psychotherapy (IACP), with a clinical background in cognitive behavioural therapy (CBT), trauma-informed care, and workplace mental health. Prior to founding AnamVR, Rob worked with clients from all walks of life — from corporate professionals and frontline healthcare workers to young adults navigating anxiety, burnout, and depression.\n\n\n\nFrustrated by the limitations of traditional therapy — long waitlists, high costs, and low engagement — Rob saw an opportunity to rethink how support could be delivered. He envisioned a model that was evidence-based, widely accessible, and actually enjoyable to use. This vision became the clinical foundation of AnamVR.\n\nRob leads the platform's therapeutic strategy, ensuring that every experience is safe, inclusive, and grounded in psychological science. He also consults directly with clients, researchers, and organisations to continuously refine AnamVR's impact.",
    image: robSheridan,
    linkedin: "https://www.linkedin.com/in/rob-sheridan/",
  },
  {
    id: "pierce-obrien",
    name: "Pierce O'Brien",
    role: "Co-Founder & CTO",
    bio: "Pierce is a game developer with a passion for serious games — interactive experiences designed to support learning, wellbeing, and meaningful engagement. With a focus on immersive media and real-time 3D development, he brings technical precision and creative intent to every aspect of AnamVR's content.\n\nAt AnamVR, Pierce leads the development of all immersive experiences, including VR scenes, user interactions, and the platform's gamification systems. He works at the intersection of game mechanics and therapeutic design, helping translate clinical goals into compelling user journeys.\n\nPierce believes that effective mental wellness tools must be not only functional, but enjoyable — a principle he carries into every build, prototype, and release.",
    image: pierceObrein,
    linkedin: "https://www.linkedin.com/in/pierceobrienireland/",
  },
  {
    id: "flor-asef",
    name: "Flor Asef",
    role: "Head of Design",
    bio: "Flor is the creative force behind AnamVR's design language and user experience. With a background in visual communication, digital product design, and branding, she ensures every element of the platform — from interface flows to social media campaigns — is cohesive, user-friendly, and emotionally engaging. \n\nFlor's approach is rooted in empathy. She designs not just for usability, but for mood regulation, accessibility, and inclusivity — key priorities for a mental wellness platform. Her design philosophy is clean, calm, and human — aligning form with function to support mental wellbeing at every touchpoint.",
    image: florAsef,
    linkedin: "https://www.linkedin.com/in/florencia-asef-arzubialde/",
  },
];

export default function TeamPage() {
  // Animation hooks for each team member card
  const teamMemberAnimations = teams.map((_, index) =>
    useScrollAnimation({ delay: index * 200 })
  );
  const headingAnimation = useScrollAnimation({ delay: 100 });
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-purple-50 to-indigo-100">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={headingAnimation.ref}
          className={`pt-40 pb-16 text-center transition-opacity duration-700 ease-out ${
            headingAnimation.isVisible ? "animate-slide-up" : "opacity-0"
          }`}
        >
          <h1 className="text-5xl md:text-6xl font-bold text-[#2b2b2b] mb-6">
            Meet the Founders Behind{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-800">
              AnamVR
            </span>
          </h1>
          <p className="text-xl text-[#2b2b2b] max-w-3xl mx-auto leading-relaxed">
            We’re a team of clinicians, developers, and designers united by a
            shared belief: mental health support should be engaging, inclusive,
            and effective.
          </p>
        </div>
      </div>

      {/* teams Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto place-items-center">
          {" "}
          {teams.map((team, index) => (
            <div
              key={team.id}
              ref={teamMemberAnimations[index].ref}
              className={`group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2 h-full flex flex-col ${
                teamMemberAnimations[index].isVisible
                  ? "animate-slide-up"
                  : "opacity-0"
              }`}
            >
              {/* Image Section */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={team.image || "/placeholder.svg"}
                  alt={team.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>

              {/* Content Section */}
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex-grow">
                  <h3 className="text-2xl font-bold text-[#2b2b2b] mb-1">
                    {team.name}
                  </h3>
                  <p className="text-lg font-semibold text-[#7745b8] mb-3">
                    {team.role}
                  </p>
                  <div className="text-gray-600 text-sm mb-4 leading-relaxed space-y-4">
                    {team.bio.split("\n\n").map((para, idx) => (
                      <p key={idx}>{para}</p>
                    ))}
                  </div>
                </div>

                {/* Bottom Social Links */}
                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                  <a
                    href={team.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
